import { nanoid, createSlice } from '@reduxjs/toolkit'
import {
  hasValidAddressDetails,
  NEW_ADDRESS_ID,
  ADDRESS_PAGE_NAMES,
} from 'utils/screens/change-address'
import { getProductSystemNameByAlias } from 'universal/cfs-card'
import { FLOWS } from 'universal/ucs'
const { CHANGE_ADDRESS } = FLOWS

const initialState = {
  // Address Flow Query Parameters.
  flowInputParams: {
    addressType: null,
    cardId: null,
    productName: null,
    addressId: null,
    webviewCallback: null,
  },

  // Card details to update billing address id.
  currentCard: {
    cardId: null,
    addressId: null,
  },

  // selected address id from existing addresses.
  selectedAddressId: null,

  // address ids array from existing addresses.
  addressIds: null,
  // nano ids array from address suggestion (validate result).
  normalizedAddressIds: null,

  // Address ID to Address map (existing addresses).
  addresses: null,
  // Nano ID (Redux) to suggested Address map (validate result).
  normalizedAddresses: null,

  // New Address object on below cases.
  // Case 1: Suggested address from normalized list.
  // Case 2: Entered address from Add Address Form.
  submittedAddress: null,

  // validateAddress API Response.
  validationResult: null,

  addressForm: {
    skip: null,
    pageError: null,
  },

  toastMessage: null,

  // Other BDMC cards to prompt for cross-card address update.
  otherBdmcCards: [],
}

const changeAddressSlice = createSlice({
  name: 'addresses',
  initialState,
  reducers: {
    initChangeAddress(state, action) {
      const {
        addressType = null,
        cardId = null,
        productName = null,
        addressId = null,
        currentCard = {},
        addresses: _addresses,
      } = action.payload || {}

      state.currentCard = {
        ...state.currentCard,
        ...currentCard,
      }

      state.selectedAddressId =
        state.selectedAddressId ||
        currentCard?.addressId ||
        addressId ||
        _addresses?.[0]?.id ||
        null

      state.flowInputParams = {
        ...state.flowInputParams,
        addressType,
        cardId,
        productName,
      }

      state.addressIds = []

      if (_addresses && Array.isArray(_addresses)) {
        state.addresses = _addresses.reduce((addressMap, address) => {
          addressMap[address.id] = address
          state.addressIds.push(address.id)
          return addressMap
        }, {})
      } else {
        state.addresses = {}
      }
    },

    addAddress(state, action) {
      const address = action.payload
      state.addresses = state.addresses || {}
      state.addressIds = state.addressIds || []
      state.addresses[address.id] = {
        ...address,
        skipAddressValidation: true,
      }

      // Add address API will return the same address Id for the same payload
      if (!state.addressIds.includes(address.id)) {
        state.addressIds = [address.id, ...state.addressIds]
      }
    },

    updateFlowInputParams(state, action) {
      const {
        addressType = null,
        cardId = null,
        productName = null,
        addressId = null,
        webviewCallback = null,
      } = action.payload || {}

      state.flowInputParams = {
        addressType,
        cardId,
        productName: getProductSystemNameByAlias(productName),
        addressId,
        webviewCallback,
      }
    },

    updateSelectedAddressId(state, action) {
      state.selectedAddressId = action.payload
    },

    updateValidationResult(state, action) {
      const { validationResult, flowName, pageName } = action.payload || {}
      const { address = {}, suggestedAddresses = [] } = validationResult || {}
      state.normalizedAddressIds = []
      const normalizedAddresses = suggestedAddresses.reduce((addressMap, suggestedAddress) => {
        const nanoId = nanoid()
        addressMap[nanoId] = suggestedAddress
        state.normalizedAddressIds.push(nanoId)
        return addressMap
      }, {})

      if (hasValidAddressDetails(address)) {
        const submitAddressIdMapping = {
          [CHANGE_ADDRESS]: state.selectedAddressId,
        }
        const submittedAddressDetails = {
          ...address,
        }
        submittedAddressDetails.id =
          pageName === ADDRESS_PAGE_NAMES.ADD_ADDRESS_PAGE
            ? NEW_ADDRESS_ID
            : submitAddressIdMapping[flowName]

        state.submittedAddress = submittedAddressDetails
      }

      state.validationResult = action.payload?.validationResult
      state.normalizedAddresses = normalizedAddresses
    },

    setToSkipAddressForm(state) {
      state.addressForm.skip = true
    },

    setPageError(state, action) {
      state.addressForm.pageError = action.payload
    },

    resetToSkipAddressForm(state) {
      state.addressForm.skip = null
    },

    resetPageError(state) {
      state.addressForm.pageError = null
    },

    resetSubmittedAddress(state) {
      state.submittedAddress = null
    },

    resetValidationResult(state) {
      state.validationResult = null
    },

    resetChangeAddress(state) {
      Object.assign(state, {
        ...initialState,
      })
    },
    setToastMessage(state, action) {
      state.toastMessage = action.payload?.toastMessage
    },
    clearToastMessage(state) {
      state.toastMessage = null
    },
    setOtherBdmcCards(state, action) {
      state.otherBdmcCards = action.payload
    },
  },
})

export const selectFlowInputParams = (state) => state.changeAddress.flowInputParams
export const selectCurrentCard = (state) => state.changeAddress.currentCard

export const selectAddressIds = (state) => state.changeAddress.addressIds
export const selectNormalizedAddressIds = (state) => state.changeAddress.normalizedAddressIds
export const selectSelectedAddressId = (state) => state.changeAddress.selectedAddressId

export const selectAddresses = (state) => state.changeAddress.addresses
export const selectNormalizedAddresses = (state) => state.changeAddress.normalizedAddresses

export const selectSubmittedAddress = (state) => state.changeAddress.submittedAddress
export const selectValidationResult = (state) => state.changeAddress.validationResult

export const selectToSkipAddressForm = (state) => state.changeAddress.addressForm.skip
export const selectPageError = (state) => state.changeAddress.addressForm.pageError

export const selectToastMessage = (state) => state.changeAddress?.toastMessage
export const selectOtherBdmcCards = (state) => state.changeAddress?.otherBdmcCards || []

export const selectCardByProductName = (state, productName) => {
  const cards = state.card?.cards || {}
  return Object.values(cards).find((card: any) => card?.productName === productName)
}

export const {
  initChangeAddress,
  addAddress,
  updateFlowInputParams,
  updateSelectedAddressId,
  updateValidationResult,
  setToSkipAddressForm,
  setPageError,
  resetToSkipAddressForm,
  resetPageError,
  resetSubmittedAddress,
  resetValidationResult,
  resetChangeAddress,
  setToastMessage,
  clearToastMessage,
  setOtherBdmcCards,
} = changeAddressSlice.actions

export default changeAddressSlice.reducer
